import React, { Fragment, Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { flow, isArray, isEqual, isObject } from 'lodash';
import Feedback from '../components/Feedback';
import Button from '../components/Button';
import { getNextPath } from '../utils/path';
import { logEvent } from '../utils/logging';
import { calculation } from '../utils/calculation';
import { isObjectEmpty } from '../utils/immutable';

import {
  storeValidationResult,
  resetValidation,
  storeCompletion,
  setAttempts,
  incrementAttempts,
  setScore,
  addScore,
  setPossibleScore,
  addQuestionToReview
} from '../store/task';
import './Validation.styles.styl';

const mapStateToProps = state => ({
  userId: state.user.userId,
  userInput: state.task.input,
  validated: state.task.validated,
  completed: state.task.completed,
  attempts: state.task.attempts,
  possibleScores: state.task.possibleScores,
  scores: state.task.scores,
  paths: state.path.paths
});

const mapDispatchToProps = dispatch => ({
  onValidation: (id, payload) => {
    dispatch(storeValidationResult(id, payload));
  },
  onValidationReset: id => {
    dispatch(resetValidation(id));
  },
  onCompletion: id => {
    dispatch(storeCompletion(id));
  },
  setAttempts: (id, attempts) => {
    dispatch(setAttempts(id, attempts));
  },
  incrementAttempts: id => {
    dispatch(incrementAttempts(id));
  },
  setScore: (id, score) => {
    dispatch(setScore(id, score));
  },
  addScore: (id, score) => {
    dispatch(addScore(id, score));
  },
  setPossibleScore: (id, score) => {
    dispatch(setPossibleScore(id, score));
  },
  addQuestion: (id, question) => {
    dispatch(addQuestionToReview(id, question));
  }
});

export default function(WrappedComponent) {
  class Validation extends Component {
    constructor(props) {
      super(props);

      const {
        match,
        setAttempts,
        attempts,
        score,
        possibleScores
      } = this.props;

      if (!attempts[match.path]) {
        setAttempts(match.path, 0);
      }

      if (possibleScores[match.path] !== score) {
        this.props.setPossibleScore(match.path, score);
      }

      this.state = {
        submitEnabled: false,
        showingCorrectAnswer: false,
        partialCreditValidator: this.defaultPartialCreditValidator,
        reviewSheetAnswers: this.defaultReviewSheet
      };
    }

    componentDidUpdate(prevProps) {
      const { match, validated } = this.props;
      if (!(match.path in prevProps.validated) && match.path in validated) {
        this.log();
      }
    }

    defaultPartialCreditValidator = () => {
      console.warn(
        'Validation HOC tried to check partial credit but no validator function has been set'
      );

      return false;
    };

    setPartialCreditValidator = validator => {
      this.setState({
        partialCreditValidator: validator
      });
    };

    defaultReviewSheet = () => {
      console.warn(
        'Validation HOC tried to set the Review Sheet question but not function has been set'
      );
      return false;
    };

    setReviewSheet = reviewSheetAnswers => {
      this.setState({
        reviewSheetAnswers
      });
    };

    getAttemptsPermitted = () => {
      return this.props.score && this.props.partiallyCorrectScoring ? 2 : 1;
    };

    isFinalAttempt = () => {
      const { attempts, match } = this.props;
      return (
        attempts[match.path] === this.getAttemptsPermitted() ||
        this.isComplete()
      );
    };

    getValidationResult = () => {
      const { match, validated } = this.props;
      return match.path in validated ? validated[match.path] : null;
    };

    getFeedback = () => {
      const { feedback, userInput, match, data } = this.props;
      const validationResult = this.getValidationResult();

      if (validationResult !== null) {
        if (this.props.data.calculation) {
          feedback.body = calculation(data, userInput[match.path], feedback);
          return feedback;
        } else if (this.props.data.questionSpecificFeedback) {
          return this.getQuestionSpecificFeedback();
        }
        if (this.isComplete()) {
          return validationResult ? feedback.success : feedback.error;
        }
        return validationResult ? feedback.success : feedback.retry;
      }
      return null;
    };

    getQuestionSpecificFeedback = () => {
      const { feedback, userInput, data, match } = this.props;
      const { question } = data;
      let answer = userInput[match.path][question];
      let answerSpecificFeedback = {};

      // string answers are for single select/radio/select objects
      if (typeof answer === 'string') {
        data[data.keys.arrayKey].map((object, i) => {
          if (object[data.keys.answerKey] === answer) {
            return (answerSpecificFeedback = feedback[i]);
          }
          return answerSpecificFeedback;
        });
      } else {
        // this could also be ` else if (typeof userInput[match.path] === object) `
        // this option is for Drag and Drop and other answers formatted as 'object'
        // look at the 'answer (userInput[match.path]'
        // look at the dropZones present (the keys for the 'answer' object
        // the answers will be an array -> see if the length is greater than the answer specific feedback
        const keys = Object.keys(userInput[match.path]);
        keys.forEach((key, i) => {
          if (userInput[match.path][key].length > data.answerThreshold) {
            answerSpecificFeedback = feedback[i];
          }
        });
        // This is for if the answers are equally distributed and we want a neutral/indifferent feedback.
        if (isObjectEmpty(answerSpecificFeedback)) {
          answerSpecificFeedback = feedback[feedback.length - 1];
        }
      }
      return answerSpecificFeedback;
    };

    determineFeedbackType = () => {
      const pathAttempts = this.props.attempts[this.props.match.path];
      const validationResult = this.getValidationResult();
      if (validationResult) {
        return 'success';
      } else if (pathAttempts !== this.getAttemptsPermitted()) {
        return 'retry';
      } else {
        return 'error';
      }
    };

    setSubmitStatus = status => {
      if (this.state.submitEnabled !== status) {
        this.setState({
          submitEnabled: status
        });
      }
    };

    isComplete = () => {
      const { completed, match } = this.props;
      return completed.includes(match.path);
    };

    handleSubmit = () => {
      const { match, onCompletion, attempts, incrementAttempts } = this.props;
      const pathAttempts = attempts[match.path];
      const totalAttempts = this.getAttemptsPermitted();
      const validationResult = this.validate();
      const correctScoreMultiplier = pathAttempts ? 0.75 : 1;
      const partiallyCorrectScoreMultiplier = 1 / totalAttempts / 2;

      if (validationResult || pathAttempts + 1 === totalAttempts) {
        if (this.props.score) {
          this.props.addQuestion(match.path, this.state.reviewSheetAnswers());
        }
        onCompletion(match.path);
      }

      if (validationResult && this.props.score) {
        // A correct answer always overrides any existing partial credit score
        const score = this.props.score * correctScoreMultiplier;
        this.props.setScore(match.path, score);
      } else if (this.props.partiallyCorrectScoring) {
        const partiallyCorrect = this.partialCreditValidate();

        if (partiallyCorrect) {
          const score = this.props.score * partiallyCorrectScoreMultiplier;
          this.props.addScore(match.path, score);
        }
      }
      incrementAttempts(match.path);
    };

    handleReset = () => {
      const { onValidationReset, match } = this.props;
      // this fires on reset, regardless of 1st or 2nd attempt.
      this.setSubmitStatus(false);
      onValidationReset(match.path);
    };

    handleSkip = event => {
      const path = this.props.match.path;
      const nextPath = getNextPath(this.props.paths, path);

      event.preventDefault();
      this.props.onCompletion(path);
      this.props.addQuestion(path, this.state.reviewSheetAnswers());

      this.props.history.push(nextPath);
      this.props.setAttempts(path, this.getAttemptsPermitted());
    };

    scrollToTop = () => {
      const root = document.getElementById('root');
      root.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    };

    prepareValues = value => {
      if (isArray(value)) {
        value.forEach((item, index, valueArray) => {
          valueArray[index] = this.prepareValues(item);
        });
        value.sort(); // Any arrays in the input need to be sorted before the comparison
      } else if (isObject(value)) {
        Object.keys(value).forEach(key => {
          value[key] = this.prepareValues(value[key]);
        });
      }

      return value;
    };

    validate = () => {
      const { correctInput, match, userInput, onValidation } = this.props;

      if (typeof correctInput === 'undefined') {
        onValidation(match.path, true);
        return;
      }
      const valuesToCompare = [userInput[match.path], correctInput];

      valuesToCompare.forEach(value => {
        this.prepareValues(value);
      });
      const result = isEqual(valuesToCompare[0], valuesToCompare[1]);

      onValidation(match.path, result);

      return result;
    };

    partialCreditValidate = () => {
      const { correctInput, match, userInput } = this.props;

      const preparedInput = this.prepareValues(userInput[match.path]);
      const preparedCorrectInput = this.prepareValues(correctInput);

      return this.state.partialCreditValidator(
        preparedInput,
        preparedCorrectInput
      );
    };

    handleAnswerShownToggle = showCorrectAnswer => {
      if (
        showCorrectAnswer &&
        (!this.isComplete() || this.getValidationResult())
      ) {
        console.error(
          'request to show correct answer made in invalid situation'
        );

        return;
      }

      this.setState({
        showingCorrectAnswer: showCorrectAnswer
      });
    };

    renderFeedbackBody = () => {
      if (this.getFeedback() === null) {
        return null;
      }

      const { match, paths } = this.props;
      const nextPath = getNextPath(paths, match.path);
      const complete = this.isComplete();

      const feedbackAnswerTogglesClasses =
        'comet-button comet-button--ghost feedback__answer-toggles__toggle';

      /* eslint-disable jsx-a11y/no-noninteractive-element-to-interactive-role */
      return (
        <Fragment>
          <p>{this.getFeedback().body}</p>
          {complete && !this.getValidationResult() && (
            <div className="feedback__answer-toggles" role="group">
              <div className="comet-button-group" role="group">
                <input
                  id="feedback__answer-toggles__toggle-user-answer"
                  type="radio"
                  className="comet-button__input"
                  name="feedback__answer-toggles"
                  checked={!this.state.showingCorrectAnswer}
                  onChange={() => {
                    this.handleAnswerShownToggle(false);
                  }}
                />
                <label
                  className={feedbackAnswerTogglesClasses}
                  role="button"
                  htmlFor="feedback__answer-toggles__toggle-user-answer"
                >
                  Your Answer
                </label>
                <input
                  id="feedback__answer-toggles__toggle-correct-answer"
                  type="radio"
                  className="comet-button__input"
                  name="feedback__answer-toggles"
                  checked={this.state.showingCorrectAnswer}
                  onChange={() => {
                    this.handleAnswerShownToggle(true);
                  }}
                />
                <label
                  className={feedbackAnswerTogglesClasses}
                  role="button"
                  htmlFor="feedback__answer-toggles__toggle-correct-answer"
                >
                  Correct Answer
                </label>
              </div>
            </div>
          )}
          {complete ? (
            <p className="feedback__primary-actions">
              <Button to={nextPath} onClick={this.scrollToTop}>
                Continue
              </Button>
            </p>
          ) : (
            <Fragment>
              <p className="feedback__primary-actions">
                <Button onClick={this.handleReset}>Try Again</Button>
              </p>

              <p className="feedback__skip">
                <a
                  className="feedback__skip__link"
                  href={nextPath}
                  onClick={this.handleSkip}
                >
                  Skip and continue to the next question.
                </a>
              </p>
            </Fragment>
          )}
        </Fragment>
      );
      /* eslint-enable jsx-a11y/no-noninteractive-element-to-interactive-role */
    };

    log = () => {
      const { attempts, userInput, match, userId, scores } = this.props;

      const feedback = this.getFeedback();
      const correct = this.getValidationResult();

      logEvent(
        userId,
        userInput[match.path],
        correct,
        attempts[match.path],
        feedback && `${feedback.title} ${feedback.body}`,
        match.path in scores ? scores[match.path] : 0
      );
    };

    render() {
      const {
        // Don't pass through
        history,
        location,
        match,
        input,
        attempts,
        score,
        onValidation,
        onValidationReset,
        onCompletion,
        isComplete,
        ...passThroughProps
      } = this.props;
      const { submitEnabled } = this.state;

      return (
        <Fragment>
          <WrappedComponent
            validationResult={this.getValidationResult()}
            setSubmitStatus={this.setSubmitStatus}
            isFinalAttempt={this.isFinalAttempt()}
            showingCorrectAnswer={this.state.showingCorrectAnswer}
            setPartialCreditValidator={this.setPartialCreditValidator}
            setReviewSheet={this.setReviewSheet}
            {...passThroughProps}
          />
          {this.getValidationResult() === null && (
            <div className="validation-submit-button-wrapper">
              <Button onClick={this.handleSubmit} disabled={!submitEnabled}>
                Submit
              </Button>
            </div>
          )}
          {this.getFeedback() !== null && (
            <Feedback
              title={this.getFeedback().title}
              body={this.renderFeedbackBody()}
              feedbackType={this.determineFeedbackType()}
            />
          )}
        </Fragment>
      );
    }
  }

  Validation.propTypes = {
    match: PropTypes.objectOf(PropTypes.any).isRequired,
    userInput: PropTypes.objectOf(PropTypes.any).isRequired,
    validated: PropTypes.objectOf(
      PropTypes.oneOfType([PropTypes.bool, PropTypes.array])
    ),
    onValidation: PropTypes.func,
    onValidationReset: PropTypes.func
  };

  return flow(
    connect(
      mapStateToProps,
      mapDispatchToProps
    ),
    withRouter
  )(Validation);
}



// WEBPACK FOOTER //
// ../core/src/hocs/Validation.js